import React, { useMemo } from 'react';
import Box from '@mui/material/Box';
import { DataTreeItem } from '../../services/api';
import { ListOptions } from '../../Views/OfflineView';
import OfflineLocSummary from './OfflineLocSummary';
import PartialOfflineLocation from './PartialOfflineLocation';

/**
 * Represents a single row for a Child Location.
 * Calculates its own sensor stats to keep the main component clean.
 */
function LocationRow({
  locId,
  allSensors,
  fullyOfflineLocs,
  listType,
  handleTagging,
}: {
  locId: string;
  allSensors: DataTreeItem[];
  fullyOfflineLocs: Set<string>;
  listType: ListOptions;
  handleTagging: (sensors: DataTreeItem[], isTagged: boolean) => void;
}) {
  // Filter sensors specific to this location or its children
  const { locSensors, offlineSensors } = useMemo(() => {
    const relevant = allSensors.filter(
      (item) => item.location === locId || item.location?.startsWith(`${locId}#`)
    );

    const offline = relevant.filter((item) => item?.value !== undefined && item.value <= 0);
    return { locSensors: relevant, offlineSensors: offline };
  }, [allSensors, locId]);

  const isFullyOffline = locSensors.length > 0 && locSensors.length === offlineSensors.length;
  const isPartiallyOffline = offlineSensors.length > 0 && !isFullyOffline;

  return (
    <Box>
      {/* 1. Full Offline Summary */}
      {isFullyOffline && (
        <OfflineLocSummary
          locId={locId}
          listType={listType}
          locSensors={locSensors}
          offlineSensors={offlineSensors}
          handleTagging={handleTagging}
        />
      )}

      {/* 2. Partial Offline Details (Sub-locations) */}
      {isPartiallyOffline && (
        <PartialOfflineLocation
          offlineSensors={offlineSensors}
          locSensors={locSensors}
          excludeLocations={fullyOfflineLocs}
          listType={listType}
          handleTagging={handleTagging}
        />
      )}
    </Box>
  );
}

export default LocationRow;
